import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { api } from "../utils/api";
import { useAuth } from "./AuthContext";

const CreditsContext = createContext();

export const useCredits = () => {
  const context = useContext(CreditsContext);
  if (!context) {
    throw new Error("useCredits must be used within a CreditsProvider");
  }
  return context;
};

export const CreditsProvider = ({ children }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // New multi-tiered credit system
  const [creditData, setCreditData] = useState({
    total: null,           // Total of all credits combined
    storybook: 0,          // Credits for creating storybooks
    character: 0,          // Credits for creating characters
    art_style: 0,          // Credits for art style transformations
    general: 0,            // General credits (can be used for anything)
    bonus: 0,              // Bonus credits from purchases/referrals
    plan: "free",
    planName: "Free"
  });

  // Legacy support - single credits value for backward compatibility
  const credits = creditData.total;

  const refreshCredits = useCallback(async () => {
    if (!user) {
      setCreditData({
        total: null,
        storybook: 0,
        character: 0,
        art_style: 0,
        general: 0,
        bonus: 0,
        plan: "free",
        planName: "Free"
      });
      return;
    }
    
    try {
      setLoading(true);
      const res = await api.get("/subscription/my-subscription");
      const data = res.data;
      
      // Extract all credit types from the response
      const storybook = data.story_credits || 0;
      const character = data.character_credits || 0;
      const art_style = data.art_style_credits || data.subscription?.art_style_credits || 0;
      const general = data.general_credits || data.monthly_credits || 0;
      const bonus = data.bonus_credits || 0;
      
      const newCreditData = {
        total: data.total_credits,
        storybook,
        character,
        art_style,
        general,
        bonus,
        plan: data.subscription?.plan || "free",
        planName: data.plan_details?.name || "Free"
      };
      
      setCreditData(newCreditData);
      return newCreditData;
    } catch (e) {
      console.error("Failed to load credits:", e);
      return null;
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Deduct credits locally for instant UI update (will be confirmed by server)
  // Now supports specific credit type deduction
  const deductCredits = useCallback((amount, creditType = null) => {
    setCreditData(prev => {
      if (prev.total === null) return prev;
      
      const newData = { ...prev, total: Math.max(0, prev.total - amount) };
      
      // Deduct from specific credit type if specified
      if (creditType && prev[creditType] !== undefined) {
        newData[creditType] = Math.max(0, prev[creditType] - amount);
      }
      
      return newData;
    });
  }, []);

  // Add credits locally (for purchases, achievements)
  const addCredits = useCallback((amount, creditType = "bonus") => {
    setCreditData(prev => {
      if (prev.total === null) return prev;
      
      const newData = { ...prev, total: prev.total + amount };
      
      // Add to specific credit type
      if (creditType && prev[creditType] !== undefined) {
        newData[creditType] = prev[creditType] + amount;
      }
      
      return newData;
    });
  }, []);

  // Legacy setCredits for backward compatibility
  const setCredits = useCallback((value) => {
    setCreditData(prev => ({ ...prev, total: value }));
  }, []);

  // Load credits when user logs in
  useEffect(() => {
    if (user) {
      refreshCredits();
    } else {
      setCreditData({
        total: null,
        storybook: 0,
        character: 0,
        art_style: 0,
        general: 0,
        bonus: 0,
        plan: "free",
        planName: "Free"
      });
    }
  }, [user, refreshCredits]);

  const value = {
    // New multi-tiered credit data
    creditData,
    setCreditData,
    // Legacy single credit value (backward compatible)
    credits,
    loading,
    refreshCredits,
    deductCredits,
    addCredits,
    setCredits
  };

  return (
    <CreditsContext.Provider value={value}>
      {children}
    </CreditsContext.Provider>
  );
};

export default CreditsContext;
